import { useEffect, useMemo, useRef, useState } from 'react';

import { useSyncController } from '../state/sync';
import { PHASES, type PhaseDescriptor } from '../sync-phases';
import type { PhaseProgress, SyncJob, SyncLogEntry } from '../types';

import { Modal } from './Modal';

/**
 * Detailed sync progress modal (spec §7.3 / §9.2).
 *
 * Layout:
 *   - Header summary (current step, elapsed, ETA, status).
 *   - Vertical phase list (every descriptor in `sync-phases.ts`). Each row
 *     shows status + last-known/running counts; click to expand for full
 *     description, target tables, and per-row sub-phase activity.
 *   - Collapsible raw event log at the bottom.
 *
 * Closing the modal does not cancel the job.
 */
export function SyncProgressModal({ onClose }: { onClose: () => void }) {
  const sync = useSyncController();
  const job = sync.job;
  const log = sync.log;
  const phases = sync.phases;

  const isTerminal = job?.status === 'completed' || job?.status === 'failed';
  const title = isTerminal
    ? job?.status === 'completed'
      ? 'Sync complete'
      : 'Sync failed'
    : job
      ? 'Syncing…'
      : 'Sync — last run';

  return (
    <Modal title={title} size="xl" onClose={onClose}>
      <div className="space-y-4">
        {job && <Header job={job} />}

        {job?.error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {job.error}
          </div>
        )}

        <div className="space-y-1">
          {PHASES.map((descriptor) => (
            <PhaseRow
              key={descriptor.name}
              descriptor={descriptor}
              progress={phases[descriptor.name]}
            />
          ))}
        </div>

        <PhaseLog log={log} />

        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button type="button" className="btn-ghost h-9" onClick={onClose}>
            {isTerminal ? 'Close' : 'Hide'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Header({ job }: { job: SyncJob }) {
  const step = job.current_step ?? '?';
  const stepsTotal = job.steps_total ?? 25;
  const phase = job.current_phase ?? 'starting';
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-semibold text-slate-900 whitespace-nowrap">
            Phase {step}/{stepsTotal}
          </span>
          <span className="text-slate-400">·</span>
          <span className="font-mono text-xs text-slate-700 truncate">
            {phase}
          </span>
        </div>
        <span
          className={
            'text-xs font-medium uppercase tracking-wide ' +
            (job.status === 'failed'
              ? 'text-rose-600'
              : job.status === 'completed'
                ? 'text-emerald-600'
                : 'text-brand-danone')
          }
        >
          {job.status}
        </span>
      </div>
      <div className="mt-1.5 grid grid-cols-3 gap-3 text-xs text-slate-600">
        <div>
          <span className="text-slate-400">Elapsed </span>
          <span className="text-slate-800 font-medium">
            {fmtMs(job.elapsed_ms)}
          </span>
        </div>
        <div>
          <span className="text-slate-400">ETA </span>
          <span className="text-slate-800 font-medium">
            {fmtMs(job.eta_ms)}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Total records </span>
          <span className="text-slate-800 font-medium">
            {job.records_done.toLocaleString()}
            {job.records_total != null
              ? ` / ${job.records_total.toLocaleString()}`
              : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

function PhaseRow({
  descriptor,
  progress,
}: {
  descriptor: PhaseDescriptor;
  progress: PhaseProgress | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const p = progress ?? {
    status: 'pending' as const,
    records_total: null,
    records_done: 0,
    records_written: null,
    elapsed_ms: null,
    eta_ms: null,
    started_at: null,
    finished_at: null,
    skipped_age_hours: null,
    sub_phases: [] as string[],
    error: null,
  };

  const hasRecords =
    (p.records_total ?? 0) > 0 || p.records_done > 0;
  const pct =
    p.records_total && p.records_total > 0
      ? Math.min(100, Math.round((p.records_done / p.records_total) * 100))
      : null;

  return (
    <div
      className={
        'border rounded-md transition-colors ' +
        (p.status === 'running'
          ? 'border-brand-danone bg-blue-50/40'
          : p.status === 'failed'
            ? 'border-rose-200 bg-rose-50/40'
            : 'border-slate-200 bg-white hover:bg-slate-50')
      }
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-danone/40 rounded-md"
      >
        <StatusIcon status={p.status} />
        <span className="text-xs font-mono text-slate-400 tabular-nums shrink-0">
          {descriptor.step.toString().padStart(2, '0')}
        </span>
        <span className="text-sm font-medium text-slate-900 min-w-0 truncate">
          {descriptor.title}
        </span>
        <span className="ml-auto text-xs text-slate-500 whitespace-nowrap shrink-0">
          {p.status === 'skipped'
            ? skippedLabel(p)
            : hasRecords
              ? recordsLabel(p)
              : p.status === 'pending'
                ? '—'
                : ''}
        </span>
        <ChevronIcon open={expanded} />
      </button>

      {p.status === 'running' && (
        <div className="px-3 pb-2">
          <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
            <div
              className="h-full bg-brand-danone transition-all"
              style={
                pct != null
                  ? { width: `${pct}%` }
                  : { width: '30%', opacity: 0.4 }
              }
            />
          </div>
        </div>
      )}

      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100 text-xs text-slate-600 space-y-2">
          <p className="leading-relaxed">{descriptor.description}</p>
          {descriptor.snTables.length > 0 && (
            <div>
              <span className="text-slate-400">ServiceNow tables: </span>
              {descriptor.snTables.map((t) => (
                <code
                  key={t}
                  className="inline-block px-1 py-px bg-slate-100 rounded text-[11px] mr-1 mb-0.5"
                >
                  {t}
                </code>
              ))}
            </div>
          )}
          {descriptor.snapshotTables.length > 0 && (
            <div>
              <span className="text-slate-400">Snapshot tables: </span>
              {descriptor.snapshotTables.map((t) => (
                <code
                  key={t}
                  className="inline-block px-1 py-px bg-slate-100 rounded text-[11px] mr-1 mb-0.5"
                >
                  {t}
                </code>
              ))}
            </div>
          )}
          {p.error && (
            <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
              {p.error}
            </div>
          )}
          {p.sub_phases.length > 0 && (
            <div>
              <span className="text-slate-400">Sub-phases: </span>
              {p.sub_phases.map((s) => (
                <code
                  key={s}
                  className="inline-block px-1 py-px bg-slate-100 rounded text-[11px] mr-1"
                >
                  {s}
                </code>
              ))}
            </div>
          )}
          {(p.records_done > 0 || p.records_total != null) && (
            <div className="text-slate-500">
              {p.records_done.toLocaleString()}
              {p.records_total != null
                ? ` / ${p.records_total.toLocaleString()}`
                : ''}{' '}
              records
              {p.elapsed_ms != null && ` · elapsed ${fmtMs(p.elapsed_ms)}`}
              {p.eta_ms != null && ` · ETA ${fmtMs(p.eta_ms)}`}
              {p.records_written != null &&
                p.records_written !== p.records_done && (
                  <> · written {p.records_written.toLocaleString()}</>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: PhaseProgress['status'] }) {
  if (status === 'completed') {
    return (
      <span
        title="Completed"
        className="w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] shrink-0"
      >
        ✓
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span
        title="Running"
        className="w-4 h-4 rounded-full border-2 border-brand-danone border-t-transparent animate-spin shrink-0"
      />
    );
  }
  if (status === 'failed') {
    return (
      <span
        title="Failed"
        className="w-4 h-4 rounded-full bg-rose-500 text-white flex items-center justify-center text-[10px] shrink-0"
      >
        ✕
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span
        title="Skipped (cached)"
        className="w-4 h-4 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-[10px] shrink-0"
      >
        ⤳
      </span>
    );
  }
  return (
    <span
      title="Pending"
      className="w-4 h-4 rounded-full border border-slate-300 shrink-0"
    />
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      className={
        'text-slate-400 transition-transform shrink-0 ' +
        (open ? 'rotate-180' : '')
      }
      aria-hidden="true"
    >
      <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function recordsLabel(p: PhaseProgress): string {
  if (p.records_total != null && p.records_total > 0) {
    return `${p.records_done.toLocaleString()} / ${p.records_total.toLocaleString()}`;
  }
  if (p.records_written != null) {
    return `${p.records_written.toLocaleString()} rows`;
  }
  if (p.records_done > 0) {
    return `${p.records_done.toLocaleString()} rows`;
  }
  return '';
}

function skippedLabel(p: PhaseProgress): string {
  const age =
    p.skipped_age_hours != null ? `${p.skipped_age_hours.toFixed(1)}h cached` : 'cached';
  if (p.records_total != null && p.records_total > 0) {
    return `${p.records_total.toLocaleString()} · ${age}`;
  }
  return age;
}

function PhaseLog({ log }: { log: SyncLogEntry[] }) {
  const [open, setOpen] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [open, log.length]);

  const summary = useMemo(() => {
    if (log.length === 0) return 'no events yet';
    const last = log[log.length - 1];
    return `${log.length} event${log.length === 1 ? '' : 's'} · last: [${last.phase}] ${last.message}`;
  }, [log]);

  return (
    <div className="rounded-md border border-slate-200">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 focus:outline-none rounded-md"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs uppercase tracking-wide text-slate-500">
          Raw event log
        </span>
        <span className="text-xs text-slate-400 truncate min-w-0 flex-1">
          {summary}
        </span>
        <ChevronIcon open={open} />
      </button>
      {open && (
        <div
          ref={logRef}
          className="max-h-56 overflow-y-auto bg-slate-50 border-t border-slate-200 text-xs font-mono p-2 space-y-0.5"
        >
          {log.length === 0 ? (
            <div className="text-slate-400">no events yet</div>
          ) : (
            log.map((e, i) => <LogLine key={i} entry={e} />)
          )}
        </div>
      )}
    </div>
  );
}

function LogLine({ entry }: { entry: SyncLogEntry }) {
  const colour =
    entry.kind === 'warning'
      ? 'text-amber-700'
      : entry.kind === 'phase_end'
        ? 'text-emerald-700'
        : entry.kind === 'phase_start'
          ? 'text-slate-900'
          : entry.kind === 'skipped'
            ? 'text-sky-700'
            : 'text-slate-600';
  return (
    <div className={`whitespace-pre-wrap break-words ${colour}`}>
      <span className="text-slate-400">[{entry.phase}]</span> {entry.message}
    </div>
  );
}

function fmtMs(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m${r.toString().padStart(2, '0')}s`;
}
